'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Upload, Loader2, ChevronRight, ChevronLeft, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import VarietyGradeBreakdown, { VarietyEntry, createDefaultVariety } from './VarietyGradeBreakdown';

interface CreateLotFormProps {
  onClose: () => void;
}

interface LotFormData {
  lotNumber: string;
  product: string;
  distributorName: string;
  distributorMobile: string;
  distributorCity: string;
  arrivalDate: string;
  totalBags: number;
  totalWeight: number;
  vehicleNumber: string;
  remarks: string;
}

const PRODUCTS = ['Onion', 'Potato', 'Tomato', 'Garlic', 'Ginger', 'Carrot', 'Beetroot', 'Cabbage', 'Cauliflower', 'Other'];

export default function CreateLotForm({ onClose }: CreateLotFormProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [varieties, setVarieties] = useState<VarietyEntry[]>([createDefaultVariety()]);
  const [gradeView, setGradeView] = useState<'official' | 'dealer'>('official');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    trigger,
  } = useForm<LotFormData>({ defaultValues: { totalBags: undefined } });

  const watchedTotalBags = watch('totalBags');
  const totalLotBags = Number(watchedTotalBags) || 0;

  const totalVarietyBags = varieties.reduce(
    (sum, v) =>
      sum + v.grades.reduce((gs, g) => gs + (gradeView === 'official' ? g.officialBags : g.dealerBags), 0),
    0
  );
  const bagsMatch = totalLotBags > 0 && totalVarietyBags === totalLotBags;

  // Validate varieties: each must have a name and at least one grade with bags > 0
  const varietiesValid = varieties.length > 0 &&
    varieties.every(
      (v) =>
        v.varietyName.trim() !== '' &&
        v.grades.length > 0 &&
        v.grades.every((g) => g.gradeName.trim() !== '')
    );

  const handleNextStep = async () => {
    const valid = await trigger(['lotNumber', 'product', 'distributorName', 'arrivalDate', 'totalBags', 'totalWeight']);
    if (valid) setStep(2);
  };

  const onSubmit = async (data: LotFormData) => {
    // Validate varieties before submit
    if (!varietiesValid) {
      toast.error('Please fill in all variety and grade names');
      return;
    }
    if (!bagsMatch) {
      toast.error('Variety bag counts must equal the total lot bags before saving');
      return;
    }

    setIsSubmitting(true);
    await new Promise((r) => setTimeout(r, 1200));
    setIsSubmitting(false);
    toast.success(`Lot #${data.lotNumber} created with ${varieties.length} variet${varieties.length === 1 ? 'y' : 'ies'}`);
    onClose();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col max-h-[80vh]">
      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-4 px-1">
        {[
          { num: 1, label: 'Lot Details' },
          { num: 2, label: 'Variety & Grades' },
        ].map((s, idx) => (
          <React.Fragment key={s.num}>
            <div className="flex items-center gap-1.5">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                  step === s.num
                    ? 'bg-primary text-white'
                    : step > s.num
                    ? 'bg-accent text-white' :'bg-muted text-muted-foreground'
                }`}
              >
                {s.num}
              </div>
              <span
                className={`text-xs font-semibold ${
                  step === s.num ? 'text-foreground' : 'text-muted-foreground'
                }`}
              >
                {s.label}
              </span>
            </div>
            {idx < 1 && <div className="flex-1 h-px bg-border" />}
          </React.Fragment>
        ))}
      </div>

      {/* ── STEP 1: Lot Header ─────────────────────────────────────────────── */}
      {step === 1 && (
        <div className="space-y-4 overflow-y-auto pr-1 flex-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-1">
              <label className="block text-xs font-semibold text-foreground mb-1.5">
                Lot Number <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                {...register('lotNumber', { required: 'Lot number is required' })}
                placeholder="e.g. 300"
                className="input-field font-mono"
              />
              {errors.lotNumber && <p className="text-2xs text-danger mt-1">{errors.lotNumber.message}</p>}
            </div>

            <div className="col-span-1">
              <label className="block text-xs font-semibold text-foreground mb-1.5">
                Product <span className="text-danger">*</span>
              </label>
              <select
                {...register('product', { required: 'Product is required' })}
                className="input-field"
              >
                <option value="">Select product</option>
                {PRODUCTS.map((p) => (
                  <option key={`product-opt-${p}`} value={p}>{p}</option>
                ))}
              </select>
              {errors.product && <p className="text-2xs text-danger mt-1">{errors.product.message}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">
                Distributor Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                {...register('distributorName', { required: 'Distributor name required' })}
                placeholder="e.g. Rajesh Traders"
                className="input-field"
              />
              {errors.distributorName && <p className="text-2xs text-danger mt-1">{errors.distributorName.message}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">Mobile Number</label>
              <input
                type="tel"
                {...register('distributorMobile', {
                  pattern: { value: /^[6-9]\d{9}$/, message: 'Invalid mobile number' },
                })}
                placeholder="9876543210"
                className="input-field font-mono"
              />
              {errors.distributorMobile && <p className="text-2xs text-danger mt-1">{errors.distributorMobile.message}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">City / Origin</label>
              <input
                type="text"
                {...register('distributorCity')}
                placeholder="e.g. Nashik"
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">
                Arrival Date <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                {...register('arrivalDate', { required: 'Arrival date required' })}
                className="input-field"
              />
              {errors.arrivalDate && <p className="text-2xs text-danger mt-1">{errors.arrivalDate.message}</p>}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">
                Total Bags <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                {...register('totalBags', {
                  required: 'Required',
                  min: { value: 1, message: 'Min 1 bag' },
                })}
                placeholder="200"
                className="input-field"
              />
              {errors.totalBags && <p className="text-2xs text-danger mt-1">{errors.totalBags.message}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">
                Total Weight (kg) <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                {...register('totalWeight', { required: 'Required', min: { value: 1, message: 'Min 1 kg' } })}
                placeholder="10000"
                className="input-field"
              />
              {errors.totalWeight && <p className="text-2xs text-danger mt-1">{errors.totalWeight.message}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">Vehicle No.</label>
              <input
                type="text"
                {...register('vehicleNumber')}
                placeholder="TN 01 AB 1234"
                className="input-field font-mono"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">Remarks</label>
            <textarea
              {...register('remarks')}
              placeholder="Quality notes, special instructions..."
              rows={2}
              className="input-field resize-none"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">Upload Documents</label>
            <div className="grid grid-cols-3 gap-2">
              {['Weighbridge Slip', 'Lorry Receipt', 'Arrival Photo'].map((docType) => (
                <button
                  key={`doc-upload-${docType}`}
                  type="button"
                  className="border-2 border-dashed border-border rounded-xl p-3 flex flex-col items-center gap-1.5 hover:border-primary/30 transition-colors"
                >
                  <Upload size={16} className="text-muted-foreground" />
                  <span className="text-2xs text-muted-foreground text-center leading-tight">{docType}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleNextStep}
              className="btn-primary flex-1"
            >
              Next: Variety &amp; Grades
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 2: Variety & Grade Breakdown ─────────────────────────────── */}
      {step === 2 && (
        <div className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto flex-1 pr-1 space-y-4">
            {/* Lot bag count reminder */}
            <div className="flex items-center justify-between bg-muted/40 rounded-xl px-4 py-2.5">
              <span className="text-xs text-muted-foreground font-medium">Lot Total Bags</span>
              <span className="text-sm font-bold font-mono text-foreground">{totalLotBags} bags</span>
            </div>

            <VarietyGradeBreakdown
              totalLotBags={totalLotBags}
              varieties={varieties}
              onChange={setVarieties}
              activeView={gradeView}
              onViewChange={setGradeView}
            />

            {/* Validation warning if not matching */}
            {!bagsMatch && totalLotBags > 0 && (
              <div className="flex items-start gap-2 bg-warning/5 border border-warning/20 rounded-xl p-3">
                <AlertTriangle size={14} className="text-warning flex-shrink-0 mt-0.5" />
                <p className="text-xs text-warning font-medium">
                  Bag counts must match before saving. Assign all {totalLotBags} bags across varieties and grades.
                </p>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4 border-t border-border mt-4">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="btn-secondary flex items-center gap-1.5"
            >
              <ChevronLeft size={14} />
              Back
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !bagsMatch || !varietiesValid}
              className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Lot'
              )}
            </button>
          </div>
        </div>
      )}
    </form>
  );
}